import React, { useEffect, useRef, useState } from 'react'
import { get } from 'helpers/api_helper'
import { useHistory, useLocation } from 'react-router-dom'
import { CompetitionPdfDownload } from "./CompetitionPresentation"
import CompetitionMenu from './CompetitionMenu'
import CompetitionConfirmation, { CompetitionOperationFeedback } from './CompetitionConfirmation'
import CompetitionPdfDialog from './CompetitionPdfDialog'
import useCompetitionOperation from './useCompetitionOperation'

export default function CompetitionActions({ id, competitionId, userDetails, returnTo, onSaved, inlinePdf = false, refreshVersion = 0 }) {
  const history = useHistory()
  const location = useLocation(), openedPdf = useRef(null)
  const [record, setRecord] = useState(null), [selected, setSelected] = useState(null), [refresh, setRefresh] = useState(0)
  const manager = useCompetitionOperation({ id, userId: userDetails?.id, onConfirmed: result => {
    if (result.status === 'saved' && result.action === 'delete') history.push(returnTo)
    else { setRefresh(value => value + 1); onSaved?.() }
  } })
  useEffect(() => {
    let active = true; setRecord(null)
    get('/admin/events/' + id + '/competition-directory').then(response => {
      if (active) setRecord({ competition: response.data?.competitions?.find(c => c._id === competitionId) })
    }).catch(() => { if (active) setRecord({ error: true }) })
    return () => { active = false }
  }, [id, competitionId, refresh, refreshVersion])
  // A chosen action, Upload/Replace PDF included, reads the competition again, and its dialog acts on that read. A registration, a payment
  // or another administrator changes the competition after this page read it, and Events refuses a command carrying the older revision
  // (COMPETITION_REVISION_CONFLICT).
  const readAgain = () => setRefresh(value => value + 1)
  const choose = action => { setSelected({ action }); setRecord(null); readAgain() }
  // An address ending in #pdf opens the PDF dialog. A read still in flight answers after the dialog opened, so only a finished one is read again.
  useEffect(() => {
    const destination = location.pathname + location.hash
    if (inlinePdf && location.hash === '#pdf' && openedPdf.current !== destination) {
      openedPdf.current = destination; setSelected({ action: 'pdf' })
      if (record) { setRecord(null); setRefresh(value => value + 1) }
    }
  }, [inlinePdf, location.pathname, location.hash, record])
  const unavailable = Boolean(record && !record.competition), pdf = selected?.action === 'pdf'
  return <div id={inlinePdf ? 'pdf' : undefined} className={inlinePdf ? 'competition-directory card card-body' : 'competition-directory p-0'}>
    {inlinePdf && <h5>Competition PDF</h5>}
    <CompetitionOperationFeedback manager={manager} />
    {!record && <span role="status">Loading actions…</span>}
    {record && !record.competition && <span role="alert">{record.error ? 'Could not load actions.' : 'Competition unavailable.'} <button type="button" className="btn btn-link" onClick={readAgain}>Retry</button></span>}
    {inlinePdf && record?.competition && <div className="mb-3">
      <CompetitionPdfDownload id={id} competition={record.competition} />
      <p className="text-muted">The PDF is saved separately from competition details. Upload failures can be retried without creating another competition.</p>
      <button type="button" className="btn btn-outline-primary mr-2" disabled={manager.pending} onClick={() => choose('pdf')}>{record.competition.competitionDetailsPdf ? 'Replace PDF' : 'Upload PDF'}</button>
      {record.competition.competitionDetailsPdf && <button type="button" className="btn btn-outline-danger" disabled={manager.pending} onClick={() => choose('removePdf')}>Remove PDF</button>}
    </div>}
    {record?.competition && <CompetitionMenu competition={record.competition} eventId={id} user={userDetails} base={'/events/' + id + '/competitions/' + competitionId} returnTo={returnTo} pending={manager.pending}
      onAction={(competition, action) => choose(action)} onPdf={inlinePdf ? undefined : () => choose('pdf')} />}
    <CompetitionConfirmation selected={selected && !pdf ? { action: selected.action, competition: record?.competition, unavailable } : null}
      manager={manager} close={() => setSelected(null)} />
    <CompetitionPdfDialog open={pdf} competition={record?.competition} unavailable={unavailable} readAgain={readAgain} manager={manager} close={() => setSelected(null)} />
  </div>
}
